<template>
    <div class="whiz-emoji-card" :class="theme">
        
        <div class="picker-container">
            <emoji-picker 
                v-show="activeTab === 'emoji'"
                ref="pickerRef"
                class="light" 
                @emoji-click="onEmojiClick"
            ></emoji-picker>

            <div v-show="activeTab === 'sticker'" class="sticker-wrapper">
                
                <div class="sticker-nav-header">
                    <button 
                        class="sticker-nav-btn" 
                        :class="{ active: stickerSubTab === 'history' }"
                        @click="stickerSubTab = 'history'"
                        title="Mais enviadas"
                    >
                        <i class="ri-time-line"></i>
                        <span>Recentes</span>
                    </button>
                    <button 
                        class="sticker-nav-btn" 
                        :class="{ active: stickerSubTab === 'library' }"
                        @click="stickerSubTab = 'library'"
                        title="Biblioteca Completa"
                    >
                        <i class="ri-gallery-view-2"></i>
                        <span>Biblioteca</span>
                    </button>
                </div>

                <div class="sticker-content custom-scrollbar">
                    
                    <div v-if="stickerSubTab === 'history'" class="sticker-grid">
                        <div v-if="isLoadingStickers" class="empty-state">
                            <i class="ri-loader-4-line"></i>
                            <p>Carregando figurinhas...</p>
                        </div>
                        <div v-else-if="recentStickers.length === 0" class="empty-state">
                            <i class="ri-time-line"></i>
                            <p>Suas figurinhas recentes aparecerão aqui.</p>
                        </div>
                        <div 
                            v-for="(sticker, index) in recentStickers" 
                            :key="sticker.id || 'recent-'+index" 
                            class="sticker-item"
                            @click="onStickerClick(sticker.url)"
                        >
                            <img :src="sticker.url" loading="lazy" />
                        </div>
                    </div>

                    <div v-else class="sticker-grid">
                        <button class="create-sticker-btn" @click="triggerFileInput" title="Criar Figurinha">
                            <div class="icon-circle">
                                <i class="ri-add-line"></i>
                            </div>
                            <span>Nova</span>
                        </button>

                        <div
                            v-for="(sticker, index) in myStickers"
                            :key="sticker.id || 'lib-'+index"
                            class="sticker-item"
                            @click="onStickerClick(sticker.url)"
                        >
                            <img :src="sticker.url" loading="lazy" />
                            <button class="delete-sticker-btn" @click.stop="deleteSticker(sticker.id)">
                                <i class="ri-close-line"></i>
                            </button>
                        </div>
                    </div>

                    <div v-if="stickerSubTab === 'library' && myStickers.length === 0 && !isLoadingStickers" class="empty-state mt-4">
                        <i class="ri-emoji-sticker-line"></i>
                        <p>Crie suas próprias figurinhas!</p>
                    </div>

                </div>

                <input 
                    type="file" 
                    ref="fileInput" 
                    class="d-none" 
                    accept="image/png, image/jpeg, image/webp" 
                    @change="handleFileSelect"
                />
            </div>
        </div>

        <div class="whiz-emoji-footer">
            <div class="nav-tabs-wrapper">
                <button 
                    class="nav-tab-btn" 
                    :class="{ active: activeTab === 'emoji' }"
                    @click="activeTab = 'emoji'"
                >
                    <i class="ri-emotion-happy-line"></i>
                    Emojis
                </button>
                <button 
                    class="nav-tab-btn" 
                    :class="{ active: activeTab === 'sticker' }"
                    @click="activeTab = 'sticker'"
                >
                    <i class="ri-emoji-sticker-line"></i>
                    Figurinhas
                </button>
            </div>
            
            <div class="skin-selector-wrapper" v-if="activeTab === 'emoji'" v-click-outside="closeSkinDropdown">
                </div>
        </div>
    </div>
</template>

<script setup>
import { ref, onMounted, watch, computed } from 'vue';
import { dataURLtoFile, useChatStore } from '@/stores/app/chatStore';
import 'emoji-picker-element';
import { toast } from 'vue3-toastify';

// Diretiva click-outside (MANTIDA)
const vClickOutside = {
    mounted(el, binding) {
        el.clickOutsideEvent = function(event) {
            if (!(el === event.target || el.contains(event.target))) {
                binding.value(event, el);
            }
        };
        document.body.addEventListener('click', el.clickOutsideEvent);
    },
    unmounted(el) {
        document.body.removeEventListener('click', el.clickOutsideEvent);
    }
};

const props = defineProps({
    theme: { type: String, default: 'light' }
});

const chatStore = useChatStore();
const emit = defineEmits(['select', 'sticker-select']);
const pickerRef = ref(null);
const fileInput = ref(null);
const activeTab = ref('emoji');
const stickerSubTab = ref('library');

const myStickers = computed(() => chatStore.myStickers);
const recentStickers = computed(() => {
    return chatStore.recentStickers;
});
const isLoadingStickers = computed(() => chatStore.isStickersLoading);

// --- LÓGICA DO PICKER ---

onMounted(() => {
    // A biblioteca vem do backend (persistida por usuário); a store cuida do
    // cache e da migração dos dados antigos de localStorage.
    chatStore.initStickers();
    if (pickerRef.value) {
        pickerRef.value.classList.toggle('dark', props.theme === 'dark');
        pickerRef.value.classList.toggle('light', props.theme === 'light');
    }
});

watch(() => props.theme, (newTheme) => {
    if (pickerRef.value) {
        pickerRef.value.classList.toggle('dark', newTheme === 'dark');
        pickerRef.value.classList.toggle('light', newTheme === 'light');
    }
});

const onEmojiClick = (event) => {
    emit('select', { i: event.detail.unicode });
};

// --- LÓGICA DE TOM DE PELE ---
// const currentSkin = ref('native');
// const currentSkinLabel = ref('');
const isSkinDropdownOpen = ref(false);

// const skinOptions = [
//     { label: 'Padrão', value: 'native', color: '#ffdd67' },
//     { label: 'Clara', value: '1', color: '#f7dece' },
//     { label: 'Morena-Clara', value: '2', color: '#f3d2a2' },
//     { label: 'Morena', value: '3', color: '#d5ab88' },
//     { label: 'Morena-Escura', value: '4', color: '#af6e51' },
//     { label: 'Escura', value: '5', color: '#744e3d' },
// ];

// const getSkinColor = (val) => skinOptions.find(o => o.value === val)?.color || '#ffdd67';

// const toggleSkinDropdown = () => isSkinDropdownOpen.value = !isSkinDropdownOpen.value;
const closeSkinDropdown = () => isSkinDropdownOpen.value = false;

// const setSkin = (val, label) => {
//     currentSkin.value = val;
//     currentSkinLabel.value = label === 'Padrão' ? '' : label;
//     isSkinDropdownOpen.value = false;

//     if (pickerRef.value) {
//         const toneMap = { 'native': 0, '1': 1, '2': 2, '3': 3, '4': 4, '5': 5 };
//         try {
//             pickerRef.value.skinTone = toneMap[val] || 0;
//         } catch (e) {
//             console.warn('Erro ao setar tom de pele:', e);
//         }
//     }
// };

// --- LÓGICA DE FIGURINHAS ---

const triggerFileInput = () => {
    if (fileInput.value) fileInput.value.click();
};

const handleFileSelect = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
        // Converte para WebP 512px e envia o arquivo ao backend — nada de base64
        // em localStorage, que era o que estourava a cota e fazia sumir a lista.
        const webpBase64 = await convertImageToWebP(file);
        const webpFile = dataURLtoFile(webpBase64, `sticker-${crypto.randomUUID()}.webp`);
        if (!webpFile) throw new Error('Falha ao converter a imagem.');

        const result = await chatStore.createStickerFromFile(webpFile);
        if (result.success) toast.success(result.message);
        else toast.info(result.message);

        stickerSubTab.value = 'library';
    } catch (e) {
        console.error(e);
        toast.error('Erro ao criar figurinha.');
    } finally {
        event.target.value = '';
    }
};

const onStickerClick = (stickerUrl) => {
    emit('sticker-select', stickerUrl);
};

const deleteSticker = async (id) => {
    try {
        await chatStore.deleteSticker(id);
    } catch {
        toast.error('Não foi possível remover a figurinha.');
    }
};

// Função Utilitária de Conversão (Client-side)
const convertImageToWebP = (file) => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = (e) => {
            const img = new Image();
            img.src = e.target.result;
            img.onload = () => {
                const canvas = document.createElement('canvas');
                const maxSize = 512;
                let width = img.width;
                let height = img.height;

                if (width > height) {
                    if (width > maxSize) {
                        height *= maxSize / width;
                        width = maxSize;
                    }
                } else {
                    if (height > maxSize) {
                        width *= maxSize / height;
                        height = maxSize;
                    }
                }

                canvas.width = 512; // Canvas quadrado padrão WA
                canvas.height = 512;
                const ctx = canvas.getContext('2d');
                
                // Centralizar
                const x = (512 - width) / 2;
                const y = (512 - height) / 2;

                ctx.drawImage(img, x, y, width, height);
                
                // Converter para WebP Base64
                const dataUrl = canvas.toDataURL('image/webp', 0.8);
                resolve(dataUrl);
            };
            img.onerror = reject;
        };
        reader.onerror = reject;
    });
};
</script>

<style scoped>
/* Container */
.whiz-emoji-card {
    display: flex;
    flex-direction: column;
    width: 370px;
    height: 460px;
    background-color: #ffffff;
    /* border-radius: 20px; */
    box-shadow: 0 12px 40px rgba(0,0,0,0.15), 0 0 0 1px rgba(0,0,0,0.06);
    overflow: visible;
    position: relative;
}

.whiz-emoji-card.dark {
    background-color: #1e1e1e;
    box-shadow: 0 12px 40px rgba(0,0,0,0.5), 0 0 0 1px rgba(255,255,255,0.1);
}

.picker-container {
    flex: 1;
    overflow: hidden;
    border-radius: 20px 20px 0 0;
    display: flex;
    flex-direction: column;
}

/* ESTILIZAÇÃO DA BIBLIOTECA (CSS Variables) 
*/
emoji-picker {
    width: 100%;
    height: 100%;
    --background: transparent;
    --border-color: transparent;
    --font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
    --text-color: #1c1c1e;
    --num-columns: 8;
    --emoji-size: 1.5rem;
    --emoji-padding: 0.4rem;
    
    /* Categorias */
    --category-emoji-size: 1.2rem;
    --category-font-color: #8e8e93;
    --category-font-color-active: #00f4b3;
    
    /* Input */
    --input-border-radius: 30px;
    --input-padding: 0.6rem 1rem;
    --outline-color: var(--bs-success);
    --outline-width: 1px;
}

/* Light Mode */
emoji-picker.light {
    --background: #ffffff;
    --input-background: #f2f2f7;
    --input-color: #1c1c1e;
    --button-hover-background: #ff088322;
    --text-color: #1c1c1e;
    --input-placeholder-color: #8e8e93;
    --category-font-color: #8e8e93;
    --category-font-color-active: var(--bs-success);
}

/* Dark Mode */
emoji-picker.dark {
    --background: #1e1e1e;
    --text-color: #ffffff;
    --input-background: #2c2c2e;
    --input-color: #ffffff;
    --category-font-color: #636366;
    --button-hover-background: rgba(255,255,255,0.1);
}

/* Esconde o footer nativo da lib */
emoji-picker::part(footer) { display: none; }
emoji-picker::part(input) { border-radius: 12px; }

/* RODAPÉ CUSTOMIZADO */
.whiz-emoji-footer {
    height: 56px;
    border-top: 1px solid #f2f2f7;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0 16px;
    background-color: #fbfbfb;
    border-radius: 0 0 20px 20px;
    flex-shrink: 0;
}
.whiz-emoji-card.dark .whiz-emoji-footer {
    background-color: #1e1e1e;
    border-top-color: #38383a;
}

.footer-label { font-size: 13px; color: #8e8e93; font-weight: 500; }
.skin-name-text { color: var(--bs-success); }

.skin-selector-wrapper { position: relative; }

.skin-btn-3d {
    width: 28px;
    height: 28px;
    border-radius: 50%;
    border: 1px solid rgba(0,0,0,0.1);
    cursor: pointer;
    box-shadow: inset -2px -2px 4px rgba(0,0,0,0.15), inset 2px 2px 4px rgba(255,255,255,0.5);
    transition: transform 0.2s;
}
.skin-btn-3d:hover { transform: scale(1.1); }

/* Dropdown */
.skin-dropdown-panel {
    position: absolute;
    bottom: 45px;
    right: -8px;
    width: 210px;
    background-color: #ffffff;
    border-radius: 14px;
    box-shadow: 0 10px 30px rgba(0,0,0,0.15), 0 0 0 1px rgba(0,0,0,0.05);
    padding: 6px 0;
    z-index: 50;
}
.whiz-emoji-card.dark .skin-dropdown-panel {
    background-color: #2c2c2e;
    box-shadow: 0 10px 30px rgba(0,0,0,0.5);
    border: 1px solid rgba(255,255,255,0.1);
}

.skin-dropdown-header {
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
    color: #8e8e93;
    padding: 6px 14px;
    border-bottom: 1px solid #f2f2f7;
    margin-bottom: 4px;
}
.whiz-emoji-card.dark .skin-dropdown-header { border-bottom-color: #38383a; }

.skin-option-item {
    display: flex;
    align-items: center;
    padding: 8px 14px;
    background: transparent;
    border: none;
    width: 100%;
    cursor: pointer;
    text-align: left;
}
.skin-option-item:hover { background-color: #f2f2f7; }
.skin-option-item.active { background-color: rgba(var(--bs-success-rgb), 0.08); }
.whiz-emoji-card.dark .skin-option-item:hover { background-color: #3a3a3c; }
.whiz-emoji-card.dark .skin-option-item.active { background-color: rgba(var(--bs-success-rgb), 0.15); }

.skin-preview-circle {
    width: 18px; height: 18px; border-radius: 50%; margin-right: 12px;
    border: 1px solid rgba(0,0,0,0.1);
}
.skin-text { font-size: 13px; color: #1c1c1e; font-weight: 500; }
.whiz-emoji-card.dark .skin-text { color: #fff; }

.check-icon { margin-left: auto; color: var(--bs-success); font-size: 16px; }

.scale-fade-enter-active, .scale-fade-leave-active { transition: all 0.2s ease; }
.scale-fade-enter-from, .scale-fade-leave-to { opacity: 0; transform: scale(0.95) translateY(10px); transform-origin: bottom right; }

/* ESTILOS DA NAVEGAÇÃO (Tabs) */
.nav-tabs-wrapper {
    display: flex;
    gap: 8px;
    flex: 1;
}

.nav-tab-btn {
    background: transparent;
    border: none;
    padding: 8px 12px;
    border-radius: 8px;
    font-size: 13px;
    font-weight: 600;
    color: #8e8e93;
    display: flex;
    align-items: center;
    gap: 6px;
    cursor: pointer;
    transition: all 0.2s;
}

.nav-tab-btn i { font-size: 16px; }

.nav-tab-btn:hover {
    background-color: rgba(0,0,0,0.05);
    color: #1c1c1e;
}
[data-bs-theme="dark"] .nav-tab-btn:hover {
    background-color: rgba(255,255,255,0.1);
    color: white;
}

.nav-tab-btn.active {
    background-color: var(--bs-success);
    color: white;
}

/* ESTILOS DA ÁREA DE FIGURINHAS */
.sticker-container {
    flex: 1;
    overflow-y: auto;
    padding: 16px;
    background-color: var(--background); /* Usa variável do componente pai se houver, ou fallback */
}

.sticker-grid {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 12px;
}

/* Botão Nova Figurinha */
.create-sticker-btn {
    aspect-ratio: 1;
    border: 2px dashed var(--bs-success);
    border-radius: 12px;
    background: transparent;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    color: var(--bs-success);
    gap: 4px;
    transition: all 0.2s;
}

.create-sticker-btn:hover {
    background-color: rgba(var(--bs-success-rgb), 0.05);
    transform: scale(1.02);
}

.create-sticker-btn .icon-circle {
    width: 24px;
    height: 24px;
    border-radius: 50%;
    background-color: var(--bs-success);
    color: white;
    display: flex;
    align-items: center;
    justify-content: center;
}

.create-sticker-btn span {
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
}

/* Item de Figurinha */
.sticker-item {
    aspect-ratio: 1;
    position: relative;
    cursor: pointer;
    border-radius: 8px;
    overflow: hidden;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 4px;
    border: 1px solid transparent;
}

.sticker-item:hover {
    background-color: rgba(0,0,0,0.05);
    border-color: rgba(0,0,0,0.1);
}

.sticker-item img {
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;
}

.delete-sticker-btn {
    position: absolute;
    top: 2px;
    right: 2px;
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background-color: rgba(0,0,0,0.5);
    color: white;
    border: none;
    display: none; /* Mostra só no hover */
    align-items: center;
    justify-content: center;
    font-size: 12px;
}

.sticker-item:hover .delete-sticker-btn { display: flex; }
.delete-sticker-btn:hover { background-color: #ef476f; }

/* Empty State */
.empty-stickers {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    height: 100%;
    color: #8e8e93;
    opacity: 0.6;
    padding-top: 40px;
}
.empty-stickers i { font-size: 48px; margin-bottom: 8px; }
.empty-stickers p { font-size: 14px; }

/* Scrollbar */
.custom-scrollbar::-webkit-scrollbar { width: 6px; }
.custom-scrollbar::-webkit-scrollbar-thumb { background-color: rgba(0,0,0,0.2); border-radius: 10px; }

.sticker-wrapper {
    display: flex;
    flex-direction: column;
    height: 100%;
    overflow: hidden;
}

.sticker-nav-header {
    display: flex;
    align-items: center;
    border-bottom: 1px solid rgba(0,0,0,0.05);
    background-color: rgba(255,255,255,0.5);
    padding: 0 8px;
    flex-shrink: 0;
}

[data-bs-theme="dark"] .sticker-nav-header {
    background-color: #1e1e1e;
}

.sticker-nav-btn {
    flex: 1;
    background: transparent;
    border: none;
    padding: 10px 0;
    font-size: 12px;
    font-weight: 600;
    color: #8e8e93;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    border-bottom: 2px solid transparent;
    transition: all 0.2s;
}

.sticker-nav-btn:hover { color: #1c1c1e; }
[data-bs-theme="dark"] .sticker-nav-btn:hover { color: white; }

.sticker-nav-btn.active {
    color: var(--bs-success);
    border-bottom-color: var(--bs-success);
}

.sticker-nav-btn i { font-size: 16px; }

.sticker-content {
    flex: 1;
    overflow-y: auto;
    padding: 12px;
}

.empty-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    text-align: center;
    opacity: 0.5;
    padding: 20px;
}
.empty-state i { font-size: 32px; margin-bottom: 8px; }
.empty-state p { font-size: 12px; }
</style>